import { useEffect, useRef, useState } from "react";
import { Suggestion, SuggestResponse } from "../types/interfaces";
import { API_BASE_URL } from "../config";

/** Wait this long after the last keystroke before asking for suggestions. */
const DEBOUNCE_MS = 200;

/** Below this, a query matches so much that the dropdown is just noise. */
const MIN_QUERY_LENGTH = 2;

/**
 * Debounced as-you-type suggestions for a search box.
 *
 * The endpoint behind this is served from the local title index and never calls
 * Watchmode, so keystrokes are free. It still debounces, because the round trip
 * is wasted work either way and a dropdown that rewrites itself mid-word is
 * harder to read than one that settles.
 *
 * In-flight requests are aborted when the query moves on, which also stops a
 * slow early response from overwriting a fast later one.
 */
export function useSuggestions(query: string, enabled: boolean = true) {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const trimmed = query.trim();

        if (!enabled || trimmed.length < MIN_QUERY_LENGTH) {
            abortRef.current?.abort();
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(async () => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/suggest/${encodeURIComponent(trimmed)}`,
                    { signal: controller.signal }
                );
                if (!response.ok) return;
                const data: SuggestResponse = await response.json();
                setSuggestions(data.suggestions || []);
            } catch (error) {
                // An abort is the normal path when typing continues, and a real
                // failure should just leave the box behaving like a plain input.
                if ((error as Error)?.name !== "AbortError") {
                    console.error("Error fetching suggestions:", error);
                }
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query, enabled]);

    // Abort whatever is in flight when the component goes away.
    useEffect(() => () => abortRef.current?.abort(), []);

    return { suggestions, clearSuggestions: () => setSuggestions([]) };
}
